// src/encounter/structured-note/note-text-line.tsx
//
// PROD-3043 · frame v2 only · encounter UX law L10 (structure first). Every free-text line of the note
// goes through here instead of mounting the DS NoteFieldLine directly, so no empty text box is ever on
// screen:
//   · written     → the DS NoteFieldLine, which reads as plain prose until it is focused (unchanged);
//   · empty, row  → the item's label and a quiet tertiary "Add" in the value position (a template text item
//                   with no value yet, e.g. Pain "Location"); "Add" opens the line, focused;
//   · empty, block→ nothing in the body; the block's header offers "Add note" (note-add-action.ts), which
//                   opens the line, focused (the block's narrative item);
//   · opened and left empty → it closes again, and nothing was written;
//   · signed (rule 38, amending or not) → no "Add" and no "Add note"; an empty line draws nothing.
// The write path, the phrases (`commands`) and the stored answer are the NoteFieldLine's, unchanged.
import { useContext, useEffect, useLayoutEffect, useRef, useState, type FocusEvent } from 'react';
import { NoteFieldLine, NoteLine, type NoteFieldCommands } from '@scripsteam/scrips-signal-ds';
import { useAddNoteAction, useHasAddNoteSlot, useNoteSigned } from './note-add-action';
import { AddEntryButton } from '../add-entry';
import { NoteLabel } from './label-parts';
import { StateTwinContext, isLoneTrigger, stateTwinLabel, useVisitNoteV2 } from './label-parts-model';

export interface NoteTextLineProps {
  id: string;
  label: string;
  value: string;
  onChange?: (next: string) => void;
  readOnly?: boolean;
  commands?: NoteFieldCommands;
  /** `row`: "Add" in the value position. `block`: the block's narrative, opened from its "Add note". */
  start?: 'row' | 'block';
}

export function NoteTextLine({ id, label, value, onChange, readOnly = false, commands, start = 'row' }: NoteTextLineProps) {
  // A "-state" twin under its base item (`StateTwinContext`, set by the note's rows): with `visitNoteV2` it reads as the reason line.
  const stateTwin = useContext(StateTwinContext);
  const signed = useNoteSigned();
  const noteV2 = useVisitNoteV2();
  // The bare trigger was typed in THIS visit (never a stored answer): only then does leaving the line clear it.
  const triggerTyped = useRef(false);
  const hasSlot = useHasAddNoteSlot();
  const [opened, setOpened] = useState(false);
  const focusOnOpen = useRef(false);
  const lineRef = useRef<HTMLDivElement>(null);
  // Visually empty (no answer, or only spaces): nothing to read, so no box either. The stored value is untouched.
  const empty = !value.trim();
  const emptyRef = useRef(empty);
  useEffect(() => {
    emptyRef.current = empty;
  });
  // Focus that went from the line into its own phrase panel and then leaves both, lands outside without this
  // line seeing a second blur: while opened and empty, any focus outside the line and its panel closes it
  // (cross-model review, round 2).
  useEffect(() => {
    if (!opened) return;
    const onFocusIn = (e: globalThis.FocusEvent) => {
      const scope = lineRef.current?.parentElement ?? lineRef.current;
      if (!scope || (e.target instanceof Node && scope.contains(e.target))) return;
      if (emptyRef.current) setOpened(false);
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [opened]);
  const canAdd = !readOnly && !signed && !!onChange;
  // A block narrative collapses only where a block header can offer its "Add note".
  const closed = empty && !opened && (start === 'row' || hasSlot);
  useAddNoteAction(
    start === 'block' && closed && canAdd,
    () => {
      focusOnOpen.current = true;
      setOpened(true);
    },
    id,
  );
  useEffect(() => {
    if (!opened || !focusOnOpen.current) return;
    focusOnOpen.current = false;
    // Within this line (not by id: for one render the header's "Add note" still carries the same anchor).
    lineRef.current?.querySelector<HTMLElement>('textarea, input, [contenteditable="true"]')?.focus();
  }, [opened]);

  // A twin shows the short label, but its text box keeps the FULL label as its name and tooltip (NoteFieldLine takes a string label
  // only, and would name the box "Not recorded because" with no item name; #963 review).
  const shortTwin = noteV2 && stateTwin;
  useLayoutEffect(() => {
    const box = lineRef.current?.querySelector<HTMLElement>('textarea, input, [contenteditable="true"]');
    // Only a twin is renamed; any other box keeps the name NoteFieldLine gives it.
    if (!box || !shortTwin) return;
    box.setAttribute('aria-label', label);
    box.setAttribute('title', label);
  });
  if (closed) {
    if (start === 'block' || !canAdd) return null;
    return (
      <div data-note-row data-empty-text-line={id}>
        <NoteLine label={<NoteLabel label={label} stateTwin={stateTwin} />} align="center">
          <AddEntryButton
            // The item's anchor (jump targets, the Sign list) lands on its "Add" while it is closed.
            id={id}
            aria-label={`Add ${label}`}
            onClick={() => {
              focusOnOpen.current = true;
              setOpened(true);
            }}
          >
            Add
          </AddEntryButton>
        </NoteLine>
      </div>
    );
  }
  // Opened and left empty (focus leaves the line with nothing typed): it closes; nothing was written. Focus
  // moving into the line's own phrase panel (its sibling, under it) is not leaving it.
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    const scope = e.currentTarget.parentElement ?? e.currentTarget;
    if (e.relatedTarget instanceof Node && scope.contains(e.relatedTarget)) return;
    // List A (`visitNoteV2`, 3 Nov: "a stray '.' … the dot-phrase trigger is leaking into the note as content"): a line left with
    // nothing but the phrase trigger ("." or "/", which opens the phrase menu) is cleared, so the trigger is never kept as the
    // answer. An answer already saved is shown as stored; only this line's own leftover trigger is cleared, on leaving it.
    if (noteV2 && triggerTyped.current && isLoneTrigger(value) && onChange && !readOnly) {
      triggerTyped.current = false;
      onChange('');
      setOpened(false);
      return;
    }
    if (!value.trim()) setOpened(false);
  };
  return (
    <div ref={lineRef} onBlur={onBlur}>
      <NoteFieldLine
        id={id}
        label={shortTwin ? stateTwinLabel(label)[0] : label}
        value={value}
        // Typing keeps the line open: clearing a written line does not close it under the caret.
        onChange={
          readOnly
            ? undefined
            : (next) => {
                if (!opened) setOpened(true);
                // Typed here: the line went from nothing to the bare trigger ("." or "/"), so leaving it may clear it.
                triggerTyped.current = isLoneTrigger(next) && (triggerTyped.current || !value.trim());
                onChange?.(next);
              }
        }
        readOnly={readOnly}
        commands={commands}
      />
    </div>
  );
}
